import { h } from 'preact';

import {
    TARGETS, ringDiameter, outerDiameter, meanPoint, zoomRing,
} from '../../lib/pistolTargets';
import { PIG_IMAGE_MM, pigPolygonPath, shotHitsPig } from '../../lib/pigZone';

// Draws positive y upwards. The .MLD sign convention is undocumented; flip this
// once if the first live Lag shows a vertically mirrored group (spec §6).
export const Y_UP = true;

// Stored x/y per millimetre. The mlleder-comp reader (Dokka's) writes tenths of
// a millimetre (megalink-agent/lib/readers/mlleder-comp.js: Math.round(mm * 10));
// the legacy-mld reader passes raw .MLD units through unconverted. The ring
// self-check on the shots page (lib/pistolTargets.js countRingMismatches) flags
// a wrong value here on the first live Lag.
export const RAW_UNITS_PER_MM = 10;

const COLOR = {
    newest: '#ff3b30', earlier: '#34c759', extra: '#8b98a9', white: '#fff', black: '#000', line: '#666',
    ellipse: '#ff9500', cep: '#5ac8fa', pig: '#ff9500',
};
// The cartoon pig (webapp/src/assets/images/pig.png) is referenced by URL like
// the other asset images (see dyreIdConnect.js), never imported.
const PIG_IMAGE_URL = './assets/images/pig.png';

// Each hit is drawn at the real calibre (5.6 mm for .22, 4.5 mm air, 9 mm
// grov), to scale with the target. That is a speck on a 500 mm duel face, so
// a thin halo ring about half a ring band wide carries the shot number.
export const HALO_RADIUS_PER_RING_STEP = 0.25;

export function hitRadius(bulletMm) {
    return bulletMm / 2;
}

export function haloRadius(target) {
    const t = TARGETS[target] || TARGETS.duel;
    return t.ringStep * HALO_RADIUS_PER_RING_STEP;
}

// SVG in millimetres, origin at the target centre. One component for every
// target: rings from lib/pistolTargets.js, hits as true-size holes with a halo.
// `zoom` (default on): the viewBox shrinks to the ring one below the worst shot
// shown (lib/pistolTargets.js zoomRing), so a tight group fills the picture.
// Geometry is unchanged — only the visible window and the line weights follow
// the zoom. The rendered <svg> carries data-zoom-ring for the tests.
// `numbered` (default on): the shot's order within the shown series is written
// inside its halo in the shot's colour (red newest, green earlier, grey extra);
// the hole itself is drawn last so it is never covered. Views that stack many
// Lag on one face (profile history) switch the numbers off.
// `overlays` (optional, mm, y up): { ellipse: { cx, cy, a, b, angleDeg }, circle: { cx, cy, r } }
// — the group ellipse and CEP circle of lib/shotAnalysis.js, drawn on top of
// everything. Geometry and zoom are unchanged by them.
// `heat` (optional): lib/shotAnalysis.js heatmapCells() — { cellMm, max, cells: [{ cx, cy, count }] }
// in mm, y up. When given, the shots are not drawn as holes; the cells are
// shaded by count instead (the zoom still follows the shots).
// `pig` (optional, Griseskyting): { cx, cy, mirrored } in mm, y up — the pig
// image and its outline (lib/pigZone.js) are drawn under the holes, and every
// counted hole is red when it touches the pig, grey when it misses (data-hit).
export default function ShotTarget({
    target = 'duel', shots = [], bulletMm = 5.6, size = '100%', yUp = Y_UP, zoom = true, numbered = true, overlays = null, heat = null,
    pig = null,
}) {
    const t = TARGETS[target] || TARGETS.duel;
    const outer = outerDiameter(target);
    const list = Array.isArray(shots) ? shots : [];
    const r = hitRadius(bulletMm);
    const halo = haloRadius(target);
    const zoomTo = zoom ? zoomRing(target, list) : null;
    const viewDiameter = zoomTo ? ringDiameter(target, zoomTo) : outer;
    const half = zoomTo ? viewDiameter / 2 + halo : outer / 2 + halo;
    const faceRadius = ringDiameter(target, t.blackFrom) / 2;
    const nineRadius = ringDiameter(target, 9) / 2;
    const guideWidth = viewDiameter / 200;
    const ringWidth = viewDiameter / 400;
    const rings = [];
    for (let value = t.lowestRing; value <= 10; value += 1) {
        const ringRadius = ringDiameter(target, value) / 2;
        const inBlack = value >= t.blackFrom;
        rings.push(
            <circle key={value} class='ring' cx='0' cy='0' r={ringRadius} fill='none' stroke={inBlack ? COLOR.white : COLOR.line} stroke-width={ringWidth} />,
        );
    }
    // Inner ten: the X-ring inside the 10 (ISSF inner-ten diameter per target).
    if (t.innerTen > 0) {
        rings.push(
            <circle key='inner' class='ring inner-ten' cx='0' cy='0' r={t.innerTen / 2} fill='none' stroke={COLOR.white} stroke-width={ringWidth} />,
        );
    }
    const labels = [];
    for (let value = t.lowestRing; value < 10; value += 1) {
        const rOuter = ringDiameter(target, value) / 2;
        const rInner = ringDiameter(target, value + 1) / 2;
        const mid = (rOuter + rInner) / 2;
        const fontSize = (rOuter - rInner) * 0.7;
        const fill = value >= t.blackFrom ? COLOR.white : COLOR.black;
        [[mid, 0], [-mid, 0], [0, mid], [0, -mid]].forEach(([x, y], i) => {
            labels.push(
                <text key={`${value}-${i}`} class='label' x={x} y={y} fill={fill} font-size={fontSize} text-anchor='middle' dominant-baseline='central'>{value}</text>,
            );
        });
    }
    const numberSize = halo * 1.15;
    const numberAt = (i, cx, cy, fill, outline) => (numbered ? (
        <text
            key={`n${i}`}
            class='shot-number'
            x={cx}
            y={cy}
            fill={fill}
            stroke={outline}
            stroke-width={numberSize / 10}
            paint-order='stroke'
            font-size={numberSize}
            font-weight='700'
            font-family='system-ui, -apple-system, sans-serif'
            text-anchor='middle'
            dominant-baseline='central'
            style='pointer-events: none;'
        >
            {i + 1}
        </text>
    ) : null);
    // Per shot: halo ring, number, then the true-size hole on top. Sighters get
    // a dashed halo and a hollow hole.
    const haloAt = (i, cx, cy, color, dashed) => (
        <circle
            key={`h${i}`}
            class={dashed ? 'halo sighter' : 'halo'}
            cx={cx}
            cy={cy}
            r={halo}
            fill='none'
            stroke={color}
            stroke-width={halo / 10}
            stroke-dasharray={dashed ? `${halo / 4} ${halo / 4}` : null}
            opacity='0.85'
        />
    );
    const holes = [];
    const heatCells = heat && Array.isArray(heat.cells) && heat.cells.length > 0 ? heat : null;
    const heatRects = heatCells ? heatCells.cells.map(cell => (
        <rect
            key={`${cell.cx}|${cell.cy}`}
            class='heat-cell'
            x={cell.cx - heatCells.cellMm / 2}
            y={cell.cy * (yUp ? -1 : 1) - heatCells.cellMm / 2}
            width={heatCells.cellMm}
            height={heatCells.cellMm}
            fill={COLOR.newest}
            opacity={0.2 + 0.8 * (cell.count / Math.max(1, heatCells.max))}
            data-count={cell.count}
        />
    )) : null;
    (heatCells ? [] : list).forEach((shot, i) => {
        const newest = i === list.length - 1;
        const cx = (shot.x || 0) / RAW_UNITS_PER_MM;
        const cy = ((shot.y || 0) / RAW_UNITS_PER_MM) * (yUp ? -1 : 1);
        if (shot.countStatus === 'sighter') {
            const ring = newest ? COLOR.newest : COLOR.earlier;
            holes.push(haloAt(i, cx, cy, ring, true));
            holes.push(numberAt(i, cx, cy, ring, 'rgba(0,0,0,0.6)'));
            holes.push(<circle key={i} class='shot sighter' data-shot={i} cx={cx} cy={cy} r={r} fill='none' stroke={ring} stroke-width={r / 2} />);
            return;
        }
        if (shot.countStatus === 'extra') {
            holes.push(haloAt(i, cx, cy, COLOR.extra, false));
            holes.push(numberAt(i, cx, cy, COLOR.extra, 'rgba(0,0,0,0.6)'));
            holes.push(<circle key={i} class='shot extra' data-shot={i} cx={cx} cy={cy} r={r} fill={COLOR.extra} />);
            return;
        }
        if (pig) {
            const hit = shotHitsPig(shot, pig, bulletMm);
            const color = hit ? COLOR.newest : COLOR.extra;
            holes.push(haloAt(i, cx, cy, color, false));
            holes.push(numberAt(i, cx, cy, color, 'rgba(0,0,0,0.6)'));
            holes.push(<circle key={i} class='shot' data-shot={i} data-hit={hit ? '1' : '0'} cx={cx} cy={cy} r={r} fill={color} />);
            return;
        }
        const color = newest ? COLOR.newest : COLOR.earlier;
        holes.push(haloAt(i, cx, cy, color, false));
        holes.push(numberAt(i, cx, cy, color, 'rgba(0,0,0,0.6)'));
        holes.push(<circle key={i} class='shot' data-shot={i} cx={cx} cy={cy} r={r} fill={color} />);
    });
    // Griseskyting: the pig under the holes. The image is centred on the
    // placement, mirrored about its own centre when the pig faces right.
    const pigFlip = yUp ? -1 : 1;
    const pigImage = pig ? (
        <image
            class='pig'
            href={PIG_IMAGE_URL}
            x={pig.cx - PIG_IMAGE_MM.w / 2}
            y={pig.cy * pigFlip - PIG_IMAGE_MM.h / 2}
            width={PIG_IMAGE_MM.w}
            height={PIG_IMAGE_MM.h}
            transform={pig.mirrored ? `translate(${2 * pig.cx} 0) scale(-1 1)` : null}
            preserveAspectRatio='none'
        />
    ) : null;
    const pigOutline = pig ? (
        <path class='pig-outline' d={pigPolygonPath(pig, yUp)} fill='none' stroke={COLOR.pig} stroke-width={guideWidth} />
    ) : null;
    // Monitor-style guides: a white horizontal line from the 9-ring out to the
    // edge of the face on both sides, and a white cross at the mean point of
    // impact of the shots on the target («Middel»). No shots, no cross. The
    // cross is drawn after the holes so a hit never hides it.
    const cross = t.tenDiameter * 0.3;
    const mpi = meanPoint(list);
    const mpiX = mpi ? mpi.x / RAW_UNITS_PER_MM : 0;
    const mpiY = mpi ? (mpi.y / RAW_UNITS_PER_MM) * (yUp ? -1 : 1) : 0;
    const guides = (
        <g class='guides' stroke={COLOR.white} stroke-width={guideWidth} stroke-linecap='butt'>
            <line x1={-faceRadius} y1='0' x2={-nineRadius} y2='0' />
            <line x1={nineRadius} y1='0' x2={faceRadius} y2='0' />
        </g>
    );
    const mpiCross = mpi ? (
        <g class='mpi-cross' stroke={COLOR.white} stroke-width={guideWidth} stroke-linecap='butt'>
            <line class='mpi' x1={mpiX - cross} y1={mpiY} x2={mpiX + cross} y2={mpiY} />
            <line class='mpi' x1={mpiX} y1={mpiY - cross} x2={mpiX} y2={mpiY + cross} />
        </g>
    ) : null;
    const flip = yUp ? -1 : 1;
    const ellipse = overlays && overlays.ellipse ? overlays.ellipse : null;
    const cepCircle = overlays && overlays.circle ? overlays.circle : null;
    const overlayWidth = guideWidth * 1.5;
    return (
        <svg viewBox={`${-half} ${-half} ${half * 2} ${half * 2}`} width={size} height={size} style='display: block;' data-zoom-ring={zoomTo || 0}>
            <circle class='paper' cx='0' cy='0' r={outer / 2} fill={COLOR.white} />
            <circle class='face' cx='0' cy='0' r={faceRadius} fill={COLOR.black} />
            {rings}
            {labels}
            {pigImage}
            {pigOutline}
            {heatRects}
            {guides}
            {holes}
            {mpiCross}
            {cepCircle && (
                <circle
                    class='overlay-cep'
                    cx={cepCircle.cx}
                    cy={cepCircle.cy * flip}
                    r={cepCircle.r}
                    fill='none'
                    stroke={COLOR.cep}
                    stroke-width={overlayWidth}
                    stroke-dasharray={`${overlayWidth * 3} ${overlayWidth * 2}`}
                />
            )}
            {ellipse && (
                <ellipse
                    class='overlay-ellipse'
                    cx={ellipse.cx}
                    cy={ellipse.cy * flip}
                    rx={ellipse.a}
                    ry={ellipse.b}
                    transform={`rotate(${ellipse.angleDeg * flip} ${ellipse.cx} ${ellipse.cy * flip})`}
                    fill='none'
                    stroke={COLOR.ellipse}
                    stroke-width={overlayWidth}
                />
            )}
        </svg>
    );
}
